import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertOctagon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export const meta = {
  id: 'monitor_alerts',
  category: 'monitoring',
  titleKey: 'dashboard.widgets.monitorAlerts',
  descriptionKey: 'dashboard.widgets.monitorAlertsDesc',
  defaultSize: 'md',
  requiresModule: 'monitor',
  icon: AlertOctagon,
};

const SEVERITY_COLORS = {
  critical: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
};

export default function MonitorAlertsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = user?.language === 'en' ? enUS : es;

  const { data } = useQuery({
    queryKey: ['monitor-alerts-dashboard'],
    queryFn: () => api.get('/monitor/alerts').then(r => r.data),
    refetchInterval: 60 * 1000,
  });

  const alerts = (data?.alerts || []).slice(0, 6);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertOctagon size={16} />
          {t('dashboard.widgets.monitorAlerts')}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/monitor')}>
          {t('dashboard.viewAll', 'View all')} →
        </button>
      </div>
      {alerts.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('dashboard.widgets.monitorAlertsEmpty', 'All clear — no active alerts')}
        </div>
      ) : (
        alerts.map(alert => {
          const color = SEVERITY_COLORS[alert.severity] || '#6b7280';
          return (
            <div
              key={alert.id}
              onClick={() => navigate('/monitor')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px',
                borderBottom: '1px solid var(--c-border-subtle)',
                cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div style={{ width: 4, height: 28, borderRadius: 2, background: color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {alert.asset_name || alert.message || t('dashboard.widgets.monitorAlertUnknown', 'Alert')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {alert.company_name || alert.alert_type || '—'}
                </div>
              </div>
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 999,
                background: color + '18', color, fontWeight: 600, flexShrink: 0,
                textTransform: 'uppercase',
              }}>
                {alert.severity}
              </span>
              {alert.created_at && (
                <span style={{ fontSize: 10, color: 'var(--c-text-subtle)', flexShrink: 0, minWidth: 60, textAlign: 'right' }}>
                  {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true, locale })}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
